import React, { useEffect, useState } from 'react';
import { Prompt, useHistory, useLocation } from 'react-router-dom';

import i18n from 'i18n-js';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import { Box, Button, IconButton, Toolbar, Tabs, Tab } from '@material-ui/core';

import { useAppSelector } from 'index';
import { Positions } from 'constants/enums';
import reservationsStore from 'store/reservationsStore';
import ActiveTab from './components/ActiveTab';
import ContactJamesTeam from './components/ContactJamesTeam';
import RequestsTab from './components/RequestsTab';
import ReservationSettings from './components/ReservationSettings';
import { PopupConfirmation } from 'components/shared';
import { RESERVATIONS_PATH, useReservationsRestaurantId, useThunkDispatch } from './hooks';
import { useStyles } from './styles';

enum ReservationsTab {
    Reservation,
    Requests,
    Active,
    Settings,
}

interface ReservationsLocationState {
    tab?: ReservationsTab;
    date?: string;
}

export const ACTIVE_TAB_STATE: ReservationsLocationState = { tab: ReservationsTab.Active };

export default function Reservations() {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation<ReservationsLocationState | undefined>();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = user.restaurants?.find(
        (item) => item.id === user.currentRestaurantId
    );
    const canEditSettings = currentRestaurant?.currentPosition === Positions.MANAGER;
    const requestsCount = useAppSelector((state) => state.reservations.requests.length);
    const [tabActive, setTabActive] = useState<ReservationsTab | null>(null);
    const [settingsDirty, setSettingsDirty] = useState(false);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
    const [pendingTab, setPendingTab] = useState<ReservationsTab | null>(null);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
    const submitSettingsRef = React.useRef<() => void>(() => undefined);
    const resetSettingsRef = React.useRef<() => void>(() => undefined);
    const dispatch = useThunkDispatch();
    const restaurantId = useReservationsRestaurantId();
    const isRequestsTab = tabActive === ReservationsTab.Requests;

    useEffect(() => {
        if (restaurantId && isRequestsTab) {
            dispatch(reservationsStore.actionCreators.getRequests(restaurantId));
        }
    }, [restaurantId, isRequestsTab]); // eslint-disable-line react-hooks/exhaustive-deps

    function handleChangeTab(_event: React.ChangeEvent<{}>, view: ReservationsTab) {
        if (view === tabActive) return;
        if (tabActive === ReservationsTab.Settings && settingsDirty) {
            setPendingTab(view);
            setPendingNavigation(null);
            setShowDiscardConfirmation(true);
            return;
        }
        setTabActive(view);
    }

    useEffect(() => {
        if (restaurant?.reservationModule?.isEnabled !== undefined) {
            setTabActive(
                !restaurant?.reservationModule?.isEnabled
                    ? ReservationsTab.Reservation
                    : location.state?.tab ?? ReservationsTab.Requests
            );
        }
    }, [restaurant?.reservationModule?.isEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    function confirmDiscard() {
        resetSettingsRef.current();
        setSettingsDirty(false);
        setShowDiscardConfirmation(false);

        if (pendingNavigation) {
            const destination = pendingNavigation;
            setPendingNavigation(null);
            setTimeout(() => history.push(destination), 0);
        } else if (pendingTab !== null) {
            setTabActive(pendingTab);
            setPendingTab(null);
        }
    }

    return tabActive !== null ? (
        <Box className={classes.reservationsRoot}>
            <Toolbar
                style={{
                    paddingBottom: '10px',
                    justifyContent: 'space-between',
                }}
            >
                <Box display="flex" alignItems="center">
                    <IconButton
                        aria-label="back"
                        size="small"
                        color="primary"
                        onClick={() => history.goBack()}
                    >
                        <ArrowBackIcon fontSize="inherit" />
                    </IconButton>
                    <Tabs
                        value={tabActive}
                        onChange={handleChangeTab}
                        aria-label="wrapped label tabs"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        {restaurant?.reservationModule?.isEnabled ? (
                            [
                                <Tab
                                    key="requests"
                                    value={ReservationsTab.Requests}
                                    label={
                                        <span className={classes.tabWithBadge}>
                                            {i18n.t('navigationTabs.requests').toUpperCase()}
                                            {requestsCount > 0 && (
                                                <span className={classes.tabBadge}>
                                                    {requestsCount}
                                                </span>
                                            )}
                                        </span>
                                    }
                                />,
                                <Tab
                                    key="active"
                                    value={ReservationsTab.Active}
                                    label={i18n.t('navigationTabs.active').toUpperCase()}
                                />,
                                <Tab
                                    key="settings"
                                    value={ReservationsTab.Settings}
                                    label={i18n.t('navigationTabs.settings').toUpperCase()}
                                />,
                            ]
                        ) : (
                            <Tab
                                value={ReservationsTab.Reservation}
                                label={i18n.t('restaurant.reservations').toUpperCase()}
                            />
                        )}
                    </Tabs>
                </Box>
                {tabActive === ReservationsTab.Active && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        style={{ marginLeft: 'auto' }}
                        onClick={() => history.push(`${RESERVATIONS_PATH}/new`)}
                    >
                        {i18n.t('reservations.newReservation')}
                    </Button>
                )}
                {tabActive === ReservationsTab.Settings && (
                    <Box display="flex" gridGap={12}>
                        <Button
                            variant="outlined"
                            color="primary"
                            disabled={!canEditSettings || !settingsDirty || settingsSaving}
                            onClick={() => {
                                resetSettingsRef.current();
                                setSettingsDirty(false);
                            }}
                        >
                            {i18n.t('restaurant.reservationSettings.cancel').toUpperCase()}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!canEditSettings || !settingsDirty || settingsSaving}
                            onClick={() => submitSettingsRef.current()}
                        >
                            {i18n.t('restaurant.reservationSettings.save').toUpperCase()}
                        </Button>
                    </Box>
                )}
            </Toolbar>
            <Prompt
                when={tabActive === ReservationsTab.Settings && canEditSettings && settingsDirty}
                message={(location) => {
                    setPendingNavigation(`${location.pathname}${location.search}${location.hash}`);
                    setPendingTab(null);
                    setShowDiscardConfirmation(true);
                    return false;
                }}
            />
            {tabActive === ReservationsTab.Reservation && <ContactJamesTeam />}
            {tabActive === ReservationsTab.Requests && <RequestsTab />}
            {tabActive === ReservationsTab.Active && (
                <ActiveTab initialDate={location.state?.date} />
            )}
            {tabActive === ReservationsTab.Settings && (
                <Box flex={1} minHeight={0} overflow="auto">
                    <ReservationSettings
                        canEdit={canEditSettings}
                        onDirtyChange={setSettingsDirty}
                        onSavingChange={setSettingsSaving}
                        submitFormRef={submitSettingsRef}
                        resetFormRef={resetSettingsRef}
                    />
                </Box>
            )}
            <PopupConfirmation
                open={showDiscardConfirmation}
                close={() => {
                    setShowDiscardConfirmation(false);
                    setPendingNavigation(null);
                    setPendingTab(null);
                }}
                title={i18n.t('restaurant.reservationSettings.discardTitle')}
                description={i18n.t('restaurant.reservationSettings.discardDescription')}
                activeBtn={i18n.t('restaurant.reservationSettings.discard')}
                action={confirmDiscard}
            />
        </Box>
    ) : null;
}
